import { effect, inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { webSocket } from 'rxjs/webSocket';
import { PhitoNotificationSubscription } from './classes/PhitoNotificationSubscription';
import { PhitoNotification } from './classes/PhitoNotification';
import { PhitoSessionService } from '../../services/phito-session.service';
import { BackendApiService } from '../../services/backend-api.service';
import { PhitoObjectApi } from '../../services/phito-object';
import { HttpParams } from '@angular/common/http';
import { PhitoUtils } from '../../utils/phito-utils';
import { AppConfigService } from '../../services/app-config.service';

@Injectable({ providedIn: 'root' })
export class NotificationCenterService {
    private config = inject(AppConfigService);
    private phitoSessionService = inject(PhitoSessionService);
    private api = inject(BackendApiService);

    wsBaseUrl: string;
    notificationSocket: Subject<any>;
    notificationPipeline: Subject<PhitoNotification>;
    notificationClickEvent = new Subject<PhitoNotification>();
    notificationOverlayOpened: Subject<boolean>;
    closeNotificationContainerEvent = new Subject<boolean>();

    constructor() {
        const config = this.config;

        this.wsBaseUrl = config.getConfig().WS_BASE_URL;

        effect(() => {
            const session = this.phitoSessionService.phitoSession();
            if (session && this.phitoSessionService.loggedIn()) {
                this.connect();
            } else {
                this.disconnect();
            }
        });

        setInterval(() => {
            if (this.notificationSocket) {
                this.notificationSocket.next((m) => {
                });
            }
        }, 30000);
    }

    watchNotificationPipeline(): Observable<PhitoNotification> {
        if (!this.notificationSocket) {
            this.connect();
        }
        if (!this.notificationPipeline) {
            this.notificationPipeline = new Subject<PhitoNotification>();
        }
        return this.notificationPipeline.asObservable();
    }

    getNotificationsUnread() {
        const params = new HttpParams().set('filter', 'read::eqb::false').set('querydata', 'false');
        return this.api.getAll('notifications', params);
    }

    getNotifications(options?: {
        read?: boolean;
        searchText?: string;
        offset?: number;
    }): Observable<PhitoObjectApi<PhitoNotification>> {
        let params = new HttpParams().set('orderby', 'createdAt::desc');
        params = params.append('limit', 40);
        if (PhitoUtils.isNNoU(options?.read)) {
            params = params.append('filter', `read::eqb::${options?.read ? 'true' : 'false'}`);
        }
        if (PhitoUtils.isNNoU(options?.searchText)) {
            params = params.append('search', options?.searchText);
        }
        if (PhitoUtils.isNNoU(options?.offset)) {
            params = params.append('offset', options?.offset);
        }
        return this.api.getAll('notifications', params, null);
    }

    triggeredNotificationOverlay(opened: boolean) {
        if (!this.notificationOverlayOpened) {
            this.notificationOverlayOpened = new Subject<boolean>();
        }
        this.notificationOverlayOpened.next(opened);
    }

    watchNotificationOverlay(): Observable<boolean> {
        if (!this.notificationOverlayOpened) {
            this.notificationOverlayOpened = new Subject<boolean>();
        }
        return this.notificationOverlayOpened.asObservable();
    }

    fireNotificationClickEvent(notification: PhitoNotification) {
        this.notificationClickEvent.next(notification);
    }

    watchNotificationClickEvent(): Observable<PhitoNotification> {
        return this.notificationClickEvent.asObservable();
    }

    markNotificationAsRead(alert: PhitoNotification) {
        return this.api.update('notifications/read/one/' + alert.id, null);
    }

    markAllNotificationAsRead() {
        return this.api.update('notifications/read/all', null);
    }

    getNotificationSettings() {
        return this.api.getAll('notification-configurations', null, null);
    }

    addSubscription(id: string, subscription: PhitoNotificationSubscription) {
        return this.api.updateGeneric('notification-configurations/subscribe/' + id, subscription.subscriptionTypes);
    }

    getNotificationConfiguration(searchTxt: any) {
        return this.api.getAll('notification-configurations', new HttpParams().set('search', `${searchTxt}`), null);
    }

    closeNotificationContainer() {
        this.closeNotificationContainerEvent.next(true);
    }

    watchCloseNotificationContainer(): Observable<boolean> {
        return this.closeNotificationContainerEvent.asObservable();
    }

    private connect() {
        if (this.notificationSocket) {
            this.disconnect();
        }
        const token = this.phitoSessionService?.xsrfToken();
        if (!token) {
            return;
        }
        const url = this.wsBaseUrl + 'wsnotification/' + token;
        this.notificationSocket = webSocket(url);

        this.notificationSocket.subscribe((msg) => {
            this.notificationPipeline.next(msg);
        });
    }

    private disconnect() {
        this.notificationSocket?.complete();
        this.notificationSocket?.unsubscribe();
    }
}
